import { gql, useQuery } from "@apollo/client";
import { MessageBar, Spinner, SpinnerSize } from "@fluentui/react";
import React, { useContext, useMemo } from "react";
import AppContext, { IFilters } from "../../../AppContext";
import Title from "../../../components/Title";
import { Entorno, Variables } from "../../../utils/constants";
import { IAnnotation } from "../../../components/BarGraphChartJS2";
import "./style.scss";
import {
  getMedidaSQL,
  mergeKeyData,
  roundGraphNew as roundGraph,
} from "../../../utils/functions";
import {
  getStepsFixed as getSteps,
  getMinFixed as getMin,
  getMaxFixed as getMax,
} from "../../../utils/functions";
import BarGraph from "../../../components/BarGraphChartJS2";
import { useTranslation } from "react-i18next";

type IValuePair = { label: string; value: number };
type IEntorno = { Totales: number; Comparables: number };
type ICadenas = {
  Nombre: string;
  Ventas: IEntorno;
  Tickets: IEntorno;
  TicketMedio: IEntorno;
  MiEspecialidad: boolean;
  MiSegmento: boolean;
  Segmentos: string[];
  Especialidades: string[];
};
export const titleEn= "Positioning map";
export const title = "Mapa de posición relativa";
export const filters: IFilters = {
  limitedPeriodo: false,
  showMedidas: true,
  hasTicketMedio: true,
  isDesglose: false,
  showEntorno: true,
  showSegmentos: true,
  showCanal: false,
};

const Graph = () => {
  const {t,i18n}=useTranslation();
  const title = t('Positioning-map') as string;
  const { semanas, filters, user } = useContext(AppContext);
  const medida = getMedidaSQL(filters.medidas);
  const entorno =
    filters.entorno === Entorno.Comparable ? "Comparables" : "Totales";

  const GET_GRAPH = gql`
    query (
      $yr1: Int!
      $wk1: Int!
      $yr2: Int!
      $wk2: Int!
      $var: Int
      $segment: String
      $segmentType: Int
    ) {
      graph(
        Graph: "Mapa de posicion relativa"
        WeekRange: {
          Start: { Year: $yr1, Week: $wk1 }
          End: { Year: $yr2, Week: $wk2 }
        }
        Filters: {
          Variables: $var
          Segmento: $segment
          SegmentType: $segmentType
        }
      )
    }
  `;

  const qry = useQuery(GET_GRAPH, {
    fetchPolicy: "no-cache",
    variables: {
      yr1: semanas[0].year,
      wk1: semanas[0].week,
      yr2: semanas[1].year,
      wk2: semanas[1].week,
      var: 1,
      segment:
        filters.segmentType === 0
          ? filters?.segmentos[filters?.segment]
          : filters?.especialidades[filters?.segment],
      segmentType: filters.segmentType,
    },
  });

  let errorMsg = t('An-error-has-occur-when-recovering-data') as string;
  let loading = qry.loading || qry.data === undefined;
  let error = !semanas || !!qry.error;
  let values: IValuePair[] = [],
    annotation: IAnnotation[] = [],
    sorted: IValuePair[] = [];

  let exportData: any = [];
  let annotationPos: "start" | "end" = "start";
  let max = 100;
  let min = 0;
  let maxSection = 0;
  const data = qry?.data?.graph;
  const mercadoValue = data?.Total ? data?.Total[medida][entorno] : 0;
  if (!!qry?.data) {
    
    try {
      if (!qry?.data?.graph)
        throw new Error(t('An-error-has-occur-when-recovering-data') as string);
      if (data?.Error) throw new Error(`${data.Error}`);

      console.warn("RESULT: ", data);
      const allData = filters.segment === Variables.Total;

      if (filters?.segmentType === 0) {
        data.Cadenas.forEach((d: ICadenas) => {
          if (
            allData ||
            d.Segmentos.includes(filters.segmentos[filters.segment])
          ) {
            if (typeof d[medida][entorno] === "number") {
              values.push({
                label: d.Nombre,
                value: Number(d[medida][entorno].toFixed(2)),
              });
            } else {
              let mierda = d[medida][entorno] as any;
              values.push({
                label: d.Nombre,
                value: Number(mierda.Totales.toFixed(2)),
              });
            }
          }
        });
      } else {
        data.Cadenas.forEach((d: ICadenas) => {
          if (
            allData ||
            d.Especialidades.includes(filters.especialidades[filters.segment])
          ) {
            if (typeof d[medida][entorno] === "number") {
              values.push({
                label: d.Nombre,
                value: Number(d[medida][entorno].toFixed(2)),
              });
            } else {
              let mierda = d[medida][entorno] as any;
              values.push({
                label: d.Nombre,
                value: Number(mierda.Totales.toFixed(2)),
              });
            }
          }
        });
      }

      if (filters.segmentType === 0) {
        Object.entries(data.Segmentos).forEach((segmento: any, i: number) => {
          if (
            allData ||
            segmento[0].includes(filters.segmentos[filters.segment])
          ) {
            maxSection =
              segmento[1][medida][entorno] > maxSection
                ? segmento[1][medida][entorno]
                : maxSection;
            annotation.push({
              label: t(segmento[0]) as string,
              type: "segmento",
              value: segmento[1][medida][entorno],
            });
          }
        });
      } else {
        Object.entries(data.Especialidades).forEach(
          (segmento: any, i: number) => {
            if (
              allData ||
              segmento[0].includes(filters.especialidades[filters.segment])
            ) {
              maxSection =
                segmento[1][medida][entorno] > maxSection
                  ? segmento[1][medida][entorno]
                  : maxSection;
              annotation.push({
                label: t(segmento[0]) as string,
                type: "segmento",
                value: segmento[1][medida][entorno],
              });
            }
          }
        );
      }

      sorted = values.sort((a, b) => {
        return b.value - a.value;
      });
        
      annotation.push({
        label: t('Market') as string,
        type: "mercado",
        value: mercadoValue,
      });
      annotationPos = "end";

      if (sorted.length > 0)
        max = roundGraph(Math.max(...sorted.map((d) => d.value)));
      if (sorted.length > 0)
        min = roundGraph(Math.min(...sorted.map((d) => d.value)));
      //  if (min > 0) min = 0;

      exportData = getExportData(data, semanas[0].year === 2019, t);
    } catch (err: any) {
      error = true;
      errorMsg = err.message ?? errorMsg;
    }
  }

  /* SE COMENTÓ ESTO PORQUE DABA PROBLEMAS DE ESCALA */
  //let m8 = Math.round(roundGraph(Math.abs(mercadoValue) * 8));
  //if (max > m8 && m8 > min) max = m8;
  //if (maxSection > max) max = roundGraph(maxSection);
  /* HASTA AQUÍ */ 
   if (max < 0) max = 0;
    //Limitamos a 100% en mapa de posicion relativa
    const AXIS_CAP = 100; if (max > AXIS_CAP) max = AXIS_CAP;

  const stepSize = useMemo(() => getSteps(min, max), [min, max]);
  const allCadenas = useMemo(
    () =>
      data === undefined || data.Cadenas === undefined
        ? []
        : data?.Cadenas.filter((cad: ICadenas) => cad.Nombre !== "").map(
            (cad: ICadenas) => cad.Nombre
          ) ?? [],
    [data]
  );

  let labels = sorted.map((d) => d.label);
  let cadenas = labels
    .filter((label) => label !== "")
    .sort((a, b) => a.localeCompare(b));

  let fmax =
    Math.round(max / stepSize) * stepSize >= max
      ? Math.round(max / stepSize) * stepSize
      : (Math.round(max / stepSize) + 1) * stepSize;

  const filteredAnnotation =
    typeof window !== "undefined" && window.location.host.includes("pt")
      ? annotation.filter(a => a.type !== "mercado")
      : annotation;

  return (
    <div className="graph-container">
      <Title
        title={title}
        error={!!error}
        files={{
          data: exportData,
          semanas,
          user,
          title,
          leyenda: "",
          transposed: false,
          secTransposed: true,
        }}
      />
      {error && <MessageBar>{errorMsg}</MessageBar>}
      {loading && (
        <div className="loading-graph">
          <Spinner size={SpinnerSize.large} />
        </div>
      )}
      {!error && (
        <BarGraph
          labels={labels}
          values={loading ? [] : [sorted.map((d) => d.value)]}
          annotation={loading ? [] : filteredAnnotation}
          annotationPos={annotationPos}
          colors={["#8c0039"]}
          highlights={
            labels.length === cadenas.length
              ? undefined
              : labels.map((l) => allCadenas.indexOf(l))
          }
          stepSize={stepSize}
          min={min}
          max={max}
          responsive
          percentages
        />
      )}
    </div>
  );
};

export default Graph;


const getExportData = (data: any, isLeap: boolean, t: any) => {
  const L = {
    chains: t("Chains") as string,
    segments: t("Segments") as string,
    total: t("Total") as string,
    totals: t("Totals") as string,
    comparables: t("Comparables") as string,
    sales: t("Sales") as string,
    tickets: t("Tickets") as string,
    avgTicket: t("Average-ticket") as string,
  };

  const labelTotals = (base: string) => `${base} ${L.totals}`;
  const labelComparables = (base: string) => `${base} ${L.comparables}`;

  
  let segmentos: any = {};
  Object.keys(data.Segmentos).forEach((segmento: string) => {
    segmentos[segmento] = isLeap
      ? {
          [labelTotals(L.sales)]: data.Segmentos[segmento].Ventas.Totales,
          [labelTotals(L.tickets)]: data.Segmentos[segmento].Tickets.Totales,
        }
      : {
          [labelTotals(L.sales)]: data.Segmentos[segmento].Ventas.Totales,
          [labelTotals(L.tickets)]: data.Segmentos[segmento].Tickets.Totales,
          [labelTotals(L.avgTicket)]: data.Segmentos[segmento].TicketMedio.Totales,
          [labelComparables(L.sales)]: data.Segmentos[segmento].Ventas.Comparables,
          [labelComparables(L.tickets)]: data.Segmentos[segmento].Tickets.Comparables,
          [labelComparables(L.avgTicket)]: data.Segmentos[segmento].TicketMedio.Comparables,
        };
  });


  segmentos[L.total] = isLeap
    ? {
        [labelTotals(L.sales)]: data.Total.Ventas.Totales,
        [labelTotals(L.tickets)]: data.Total.Tickets.Totales,
      }
    : {
        [labelTotals(L.sales)]: data.Total.Ventas.Totales,
        [labelTotals(L.tickets)]: data.Total.Tickets.Totales,
        [labelTotals(L.avgTicket)]: data.Total.TicketMedio.Totales,
        [labelComparables(L.sales)]: data.Total.Ventas.Comparables,
        [labelComparables(L.tickets)]: data.Total.Tickets.Comparables,
        [labelComparables(L.avgTicket)]: data.Total.TicketMedio.Comparables,
      };


  let names = data.Cadenas.map((d: ICadenas) => d.Nombre);

  const exportData: any[] = [];

  exportData.push(
    isLeap
      ? {
          [L.chains]: {
            [labelTotals(L.sales)]: {
              ...mergeKeyData(
                names,
                data.Cadenas.map((d: any) => d.Ventas.Totales)
              ),
            },
            [labelTotals(L.tickets)]: {
              ...mergeKeyData(
                names,
                data.Cadenas.map((d: any) => d.Tickets.Totales)
              ),
            },
          },
        }
      : {
          [L.chains]: {
            [labelTotals(L.sales)]: {
              ...mergeKeyData(
                names,
                data.Cadenas.map((d: any) => d.Ventas.Totales)
              ),
            },
            [labelTotals(L.tickets)]: {
              ...mergeKeyData(
                names,
                data.Cadenas.map((d: any) => d.Tickets.Totales)
              ),
            },
            [labelTotals(L.avgTicket)]: {
              ...mergeKeyData(
                names,
                data.Cadenas.map((d: any) => d.TicketMedio.Totales)
              ),
            },
            [labelComparables(L.sales)]: {
              ...mergeKeyData(
                names,
                data.Cadenas.map((d: any) => d.Ventas.Comparables)
              ),
            },
            [labelComparables(L.tickets)]: {
              ...mergeKeyData(
                names,
                data.Cadenas.map((d: any) => d.Tickets.Comparables)
              ),
            },
            [labelComparables(L.avgTicket)]: {
              ...mergeKeyData(
                names,
                data.Cadenas.map((d: any) => d.TicketMedio.Comparables)
              ),
            },
          },
        }
  );


  exportData.push({
    [L.segments]: { ...segmentos },
  });

 
  exportData.push({
    sKeys: data.Cadenas.map((d: any) => d.Segmentos[0]),
  });

  exportData.push({
    isMapaPosicionRelativa: true,
  });

  return exportData;
};
